/**
 * A setup question, asked by the doorman in its own thread (owner decision
 * 2026-08-05, extended course): the criteria first, then who the owner is,
 * what the doorman is called, how it sounds, and how many knocks a day it
 * takes. The door is ALREADY live — these only tune it, and they say so.
 *
 * Chips are suggestions FOR the chat: a multi-pick chip appends to the
 * composer draft, a single-pick chip replaces it, and a text question just
 * asks — the composer is the answer box either way. "I'm done — leave it
 * open" ends the course for good; Skip sets one question aside for this
 * sitting.
 */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { applyDoormanSetup, type SetupQuestion as SetupQuestionDef } from "../../lib/api";
import { qk } from "../../lib/queries";
import { DoormanBubble } from "../knock/chat";
import { QuietButton } from "../ui/button";

export const QUESTION_COPY: Record<SetupQuestionDef["key"], string> = {
  gets: "Your door is already answering. What should get through? Tap a suggestion to draft it — edit, add your own, send.",
  doesnt: "And what should I turn away? Tap to draft it, or say it.",
  // Ruling R-0908-2: this answer is the BIO, not a rules section. The copy says
  // where it lands, because "the top of your rules" was true and is not.
  who: "Who are you? A line or two, in your own words — it is what your door says about you.",
  name: "What should I be called? One or two words — mine to wear.",
  tone: "And how should I sound? Tap one, or say it your way.",
  cap: "At most, how many knocks a day should I take? Tap one, or give me a number.",
};

/** The composer speaks the question's language while one is active. */
export const PLACEHOLDER_COPY: Record<SetupQuestionDef["key"], string> = {
  gets: "Ask your doorman.",
  doesnt: "Ask your doorman.",
  who: "A line or two about you…",
  name: "Name your doorman…",
  tone: "How should I sound?",
  cap: "A number, or “no cap”…",
};

export const DONE_COPY = "I'm done — leave it open";

type Props = {
  question: SetupQuestionDef;
  /** Draft the label into the composer — the chat is the mechanism. */
  onPick: (label: string, mode: "append" | "replace") => void;
  /** Labels already drafted, so a chip is not offered twice. */
  drafted: ReadonlySet<string>;
  onSkip: () => void;
  disabled: boolean;
};

export function SetupQuestion({ question, onPick, drafted, onSkip, disabled }: Props) {
  const queryClient = useQueryClient();
  const [ending, setEnding] = useState(false);

  const done = () => {
    if (ending) return;
    setEnding(true);
    void applyDoormanSetup({ done: true }).then(async (r) => {
      setEnding(false);
      if (!r.ok) return;
      await queryClient.invalidateQueries({ queryKey: qk.doormanThread() });
    });
  };

  const chips = "chips" in question ? question.chips : [];
  const mode = question.kind === "chips_one" ? ("replace" as const) : ("append" as const);

  return (
    <div data-testid="setup-question">
      <DoormanBubble>{QUESTION_COPY[question.key]}</DoormanBubble>
      <div className="mt-3 flex flex-wrap items-center gap-2 pl-1">
        {chips.map((chip) => {
          const used = mode === "append" && drafted.has(chip.label);
          return (
            <button
              key={chip.label}
              type="button"
              disabled={disabled || ending || used}
              onClick={() => onPick(chip.label, mode)}
              className="inline-flex h-9 items-center rounded-lg border border-border-strong px-3
                         text-[13px] font-medium text-text-soft transition-colors duration-75
                         hover:border-accent/60 hover:text-accent-ink-strong
                         focus-visible:outline-none focus-visible:ring-2
                         focus-visible:ring-focus/70 focus-visible:ring-offset-2
                         focus-visible:ring-offset-bg disabled:pointer-events-none
                         disabled:opacity-40"
            >
              {chip.label}
            </button>
          );
        })}
        <QuietButton type="button" onClick={onSkip} disabled={disabled || ending}>
          Skip
        </QuietButton>
        <QuietButton type="button" onClick={done} disabled={disabled || ending}>
          {DONE_COPY}
        </QuietButton>
      </div>
    </div>
  );
}
